// FILE: src/components/app/bases/BudgetBaseComponent/BudgetAddForm/BudgetAddForm.js
import React from "react";
import PropTypes from "prop-types";
import styles from "./BudgetAddForm.module.css";

import { TagsAPI, TransactionsAPI } from "../../../../../utils/firebase";
import { normalizeFullPath } from "../../UploadDataComponent/utils/tagUtils";
import { DescriptionTypeaheadInput, TagsMultiTypeaheadInput } from "../../TransactionsBaseComponent/utils/TypeaheadInputs";

function coerceTagArrayFromComma(val) {
  return String(val || "")
    .split(",")
    .map((s) => normalizeFullPath(s.trim()))
    .filter(Boolean);
}

export default function BudgetAddForm({
  user,
  mode = "create",
  initialValue,
  accounts = [],
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = React.useState({
    name: "",
    description: "",
    targetDate: "",
    amount: "",
    bufferPct: 10,
    sourceType: "networth",
    accountId: accounts[0]?.id || "",
    tagsInput: "",
  });

  // keep account list in sync with prop
  React.useEffect(() => {
    setForm((f) => ({
      ...f,
      accountId: f.sourceType === "account" ? (f.accountId || accounts[0]?.id || "") : "",
    }));
  }, [accounts]);

  React.useEffect(() => {
    if (!initialValue) return;
    setForm((f) => ({
      ...f,
      name: initialValue.name || "",
      description: initialValue.description || "",
      targetDate: initialValue.targetDate || "",
      amount: String(initialValue.amount ?? ""),
      bufferPct: Number(initialValue.bufferPct ?? 10),
      sourceType: initialValue.source?.type || "networth",
      accountId: initialValue.source?.accountId || accounts[0]?.id || "",
      tagsInput: Array.isArray(initialValue.tags) ? initialValue.tags.join(", ") : "",
    }));
  }, [initialValue, accounts]);

  // ---- typeahead suggestions ----
  const [tagSuggestions, setTagSuggestions] = React.useState([]);
  const [descSuggestions, setDescSuggestions] = React.useState([]);

  // subscribe tags
  React.useEffect(() => {
    if (!user?.email) { setTagSuggestions([]); return; }
    const unsub = TagsAPI.subscribeUserTags({
      userEmail: user.email,
      cap: 1000,
      onData: (docs) => {
        try {
          const fulls = [];
          for (const t of docs || []) {
            const p = normalizeFullPath(t.fullPath || t.name || "");
            if (p) fulls.push(p);
          }
          fulls.sort((a, b) => a.localeCompare(b));
          setTagSuggestions(fulls);
        } catch {
          setTagSuggestions([]);
        }
      },
      onError: () => setTagSuggestions([]),
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [user]);

  // description suggestions (recent unique)
  React.useEffect(() => {
    let alive = true;
    async function loadDescs() {
      if (!user?.email) { setDescSuggestions([]); return; }
      try {
        const rows = await TransactionsAPI.fetchUserTransactionsRange({
          userEmail: user.email,
          cap: 500,
          latestFirst: true,
        });
        if (!alive) return;
        const seen = new Set();
        const uniq = [];
        for (const r of Array.isArray(rows) ? rows : []) {
          const d = (r.description || "").trim();
          if (!d) continue;
          const dl = d.toLowerCase();
          if (!seen.has(dl)) {
            seen.add(dl);
            uniq.push(d);
          }
          if (uniq.length >= 100) break;
        }
        setDescSuggestions(uniq);
      } catch {
        if (alive) setDescSuggestions([]);
      }
    }
    loadDescs();
    return () => { alive = false; };
  }, [user]);

  const canSubmit = form.name && form.targetDate && Number(form.amount) > 0;

  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const payload = {
      ...(initialValue || {}),
      name: String(form.name).trim(),
      description: String(form.description).trim(),
      targetDate: form.targetDate,
      amount: Number(form.amount),
      bufferPct: Number(form.bufferPct || 0),
      source:
        form.sourceType === "account"
          ? { type: "account", accountId: form.accountId || accounts[0]?.id || null }
          : { type: "networth" },
      tags: coerceTagArrayFromComma(form.tagsInput),
    };

    onSubmit?.(payload);
  };

  return (
    <form onSubmit={submit} className={styles.BudgetAddForm}>
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Name</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Summer trip"
            required
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">When</label>
          <input
            type="date"
            className="form-control"
            value={form.targetDate}
            onChange={(e) => setForm((f) => ({ ...f, targetDate: e.target.value }))}
            required
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="form-control"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="1500"
            required
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Buffer ±%</label>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            className="form-control"
            value={form.bufferPct}
            onChange={(e) => setForm((f) => ({ ...f, bufferPct: e.target.value }))}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Description</label>
          <DescriptionTypeaheadInput
            value={form.description}
            onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            suggestions={descSuggestions}
            placeholder="Notes (hotels, flights, gear...)"
            maxMatches={5}
            minChars={2}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Tags</label>
          <TagsMultiTypeaheadInput
            value={form.tagsInput}
            onChange={(v) => setForm((f) => ({ ...f, tagsInput: v }))}
            suggestions={tagSuggestions}
            placeholder="Comma-separated. e.g., Travel, Family / Summer"
            maxMatches={5}
            minChars={1}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Funding source</label>
          <div className="d-flex gap-2">
            <select
              className="form-select"
              value={form.sourceType}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  sourceType: v,
                  accountId: v === "account" ? (f.accountId || accounts[0]?.id || "") : "",
                }));
              }}
            >
              <option value="networth">From overall net worth</option>
              <option value="account">From a specific account</option>
            </select>
            <select
              className="form-select"
              disabled={form.sourceType !== "account"}
              value={form.accountId}
              onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-12 d-flex gap-2">
          <button type="submit" className="btn btn-primary btn-compact" disabled={!canSubmit}>
            {mode === "edit" ? "Save changes" : "Add budget item"}
          </button>
          <button type="button" className="btn btn-outline-primary btn-compact" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

BudgetAddForm.propTypes = {
  user: PropTypes.object,
  mode: PropTypes.oneOf(["create", "edit"]),
  initialValue: PropTypes.object,
  accounts: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};
